import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  InputAdornment,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack as ArrowBackIcon,
  Coffee as CoffeeIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const ResetPassword = ({keywordsData,language}) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [userInfo, setUserInfo] = useState(null);

  // Verify token on mount
  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.post('/password-reset/verify-token', { token });
      setUserInfo(response.data.user);
      setVerifying(false);
    } catch (err) {
      setError(err.response?.data?.message || language ==='ar' ?  'الرابط غير صالح أو منتهي الصلاحية' : 'The link is invalid or expired');
      setVerifying(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/password-reset/reset', {
        token,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password
      });

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      setError(err.response?.data?.message || language ==='ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى':'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container maxWidth="sm" sx={{ 
        py: 8,
        px: 2,
        mb:10,
        backgroundColor: '#000000',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper sx={{ 
          p: 5,
          width: '100%',
          backgroundColor: '#0D0D0D',
          border: '1px solid #2D2D2D',
          borderRadius: '24px',
          textAlign: 'center'
        }}>
          <CircularProgress size={60} sx={{ color: '#FFD700', mb: 3 }} />
          <Typography sx={{ color: '#B3B3B3' }}>
            {language ==='ar' ?  'جاري التحقق من الرابط...' : 'Checking link ...'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ 
      py: 8,
      px: 2,
      backgroundColor: '#000000',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Paper sx={{ 
        p: { xs: 3, sm: 5 },
        width: '100%',
        backgroundColor: '#0D0D0D',
        border: error ? '1px solid #FF3D00' : (success ? '1px solid #4CAF50' : '1px solid #FFD700'),
        borderRadius: '24px',
        boxShadow: error ? '0 8px 32px rgba(255, 61, 0, 0.15)' : (success ? '0 8px 32px rgba(76, 175, 80, 0.15)' : '0 8px 32px rgba(255, 215, 0, 0.15)'),
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          opacity: 0.1,
          transform: 'rotate(15deg)'
        }}>
          <CoffeeIcon sx={{ fontSize: 80, color: error ? '#FF3D00' : (success ? '#4CAF50' : '#FFD700') }} />
        </Box>

        {/* Back to Login Link */}
        {!success && (
          <Box sx={{ mb: 3 }}>
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: '#B3B3B3',
                textDecoration: 'none',
                fontSize: '0.95rem',
                '&:hover': {
                  color: '#FFD700'
                }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              {language ==='ar' ?  'العودة إلى تسجيل الدخول' : 'Back to login'}
            </Link>
          </Box>
        )}

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: error ? 'rgba(255, 61, 0, 0.1)' : (success ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 215, 0, 0.1)'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            border: `2px solid ${error ? '#FF3D00' : (success ? '#4CAF50' : '#FFD700')}`
          }}>
            {success ? (
              <CheckCircleIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
            ) : (
              <LockIcon sx={{ fontSize: 40, color: error ? '#FF3D00' : '#FFD700' }} />
            )}
          </Box>
          
          {success ? (
            <>
              <Typography variant="h4" sx={{ 
                color: '#4CAF50',
                fontWeight: 900,
                mb: 1
              }}>
                {language ==='ar' ?  'تم بنجاح!' : 'Successfully!'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
                {language ==='ar' ?  'تم تغيير كلمة المرور الخاصة بك' : 'Your password has been changed'}
              </Typography>
            </>
          ) : error ? (
            <>
              <Typography variant="h4" sx={{ 
                color: '#FF3D00',
                fontWeight: 900,
                mb: 1
              }}>
                {language ==='ar' ?  'خطأ' : 'Error'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#B3B3B3' }}>
                {error}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h4" sx={{ 
                color: '#FFD700',
                fontWeight: 900,
                letterSpacing: '2px',
                mb: 1
              }}>
                {language ==='ar' ?  'إعادة تعيين كلمة المرور' : 'Reset password'}
              </Typography>
              {userInfo && (
                <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                  {language ==='ar' ?  `مرحباً ${userInfo.username}، أدخل كلمة المرور الجديدة` : `Hello ${userInfo.username}, enter your new password`}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Error Message */}
        {error && !success && (
          <Alert 
            severity="error"
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(255, 61, 0, 0.1)',
              color: '#FF3D00',
              border: '1px solid #FF3D00',
              borderRadius: '12px'
            }}
          >
            {error}
            <Box sx={{ mt: 2 }}>
              <Button
                component={RouterLink}
                to="/forgot-password"
                variant="outlined"
                size="small"
                sx={{
                  color: '#FFD700',
                  borderColor: '#FFD700',
                  '&:hover': {
                    borderColor: '#FFE55C',
                    backgroundColor: 'rgba(255, 215, 0, 0.08)',
                  }
                }}
              >
                {language ==='ar' ?  'طلب رابط جديد' : 'Request a new link'}
              </Button>
            </Box>
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Box sx={{ textAlign: 'center' }}>
            <Alert 
              severity="success"
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                color: '#4CAF50',
                border: '1px solid #4CAF50',
                borderRadius: '12px'
              }}
            >
              {language ==='ar' ?  'سيتم تحويلك إلى صفحة تسجيل الدخول خلال 3 ثوان...' : 'You will be redirected to the login page in 3 seconds...'}
            </Alert>
            
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#FFE55C'
                }
              }}
            >
              {language ==='ar' ?  'تسجيل الدخول الآن' : 'Login'}
            </Button>
          </Box>
        )}

        {/* Form */}
        {!error && !success && (
          <Box component="form" onSubmit={handleSubmit}>
            {/* New Password */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ color: '#B3B3B3', mb: 1, textAlign: 'right' }}>
                {language ==='ar' ?  'كلمة المرور الجديدة' : 'New password'}
              </Typography>
              <TextField
                fullWidth
                placeholder={language ==='ar' ?  'أدخل كلمة المرور الجديدة' : 'Enter new password'}
                type={showPassword ? 'text' : 'password'}
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#B3B3B3' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: '#2D2D2D',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FFD700',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                      borderWidth: '2px',
                    },
                  },
                }}
              />
            </Box>

            {/* Confirm Password */}
            <Box sx={{ mb: 4 }}>
              <Typography sx={{ color: '#B3B3B3', mb: 1, textAlign: 'right' }}>
                {language ==='ar' ?  'تأكيد كلمة المرور' : 'Confirm password'}
              </Typography>
              <TextField
                fullWidth
                placeholder={language ==='ar' ?  'أعد إدخال كلمة المرور' : 'Re-enter your password'}
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: '#B3B3B3' }}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: '#2D2D2D',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FFD700',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                      borderWidth: '2px',
                    },
                  },
                }}
              />
            </Box>

            {/* Password Requirements */}
            <Box sx={{ 
              mb: 3,
              p: 2,
              backgroundColor: '#1A1A1A',
              borderRadius: '12px',
              border: '1px solid #2D2D2D'
            }}>
              <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 1 }}>
                {language ==='ar' ?  'متطلبات كلمة المرور:' : 'Password requirements:'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ 
                  color: formData.new_password.length >= 6 ? '#4CAF50' : '#B3B3B3',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  {formData.new_password.length >= 6 ? '✓' : '•'} {language ==='ar' ?  '6 أحرف على الأقل' : 'At least 6 letters'}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: (formData.new_password && formData.new_password === formData.confirm_password) ? '#4CAF50' : '#B3B3B3',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  {(formData.new_password && formData.new_password === formData.confirm_password) ? '✓' : '•'} {language ==='ar' ?  'تطابق كلمات المرور' : 'Password match'}
                </Typography>
              </Box>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ 
                py: 1.8,
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 800,
                fontSize: '1.1rem',
                borderRadius: '12px',
                border: '1px solid #FFD700',
                '&:hover': {
                  backgroundColor: '#FFE55C',
                  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                  transform: 'translateY(-2px)'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#2D2D2D',
                  color: '#6B6B6B',
                  border: '1px solid #2D2D2D',
                },
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: '#000000' }} />
                  <span>{language ==='ar' ?  'جاري التغيير...' : 'Changing passsword...'}</span>
                </Box>
              ) : (
                language ==='ar' ?  'تغيير كلمة المرور' : 'Change password'
              )}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ResetPassword;